/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent, useState } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import { Row, Col, Select, Input, Divider, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { NextSeo, SiteLinksSearchBoxJsonLd } from 'next-seo';
import BasicLayout from 'layout/Basic';
import { convertProps } from 'lib/parseAPIResponse';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import { Rectangle, HalfPage, ExpandNavibox, SkyscraperMobile, Wideboard } from 'components/Ads';
import Section, { SectionTitle } from 'components/Section';
import Subscription from 'containers/Subscription';
import Newsletter from 'containers/Newsletter';
import Pagination from 'components/Pagination';
import createFromObject from 'lib/queryString';
import Item from 'components/Card';
import ContentProvidersSection from 'components/ContentProvidersSection';
import EventsBlock from 'components/EventsBlock';

const { Option, OptGroup } = Select;

const Search = styled(Input)`
    border-width: 0 0 1px 0;
`;

const SearchText = styled.span`
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 2.22;
    letter-spacing: 0.36px;
    text-align: left;
    color: ${(props) => props.theme.colors.greyishBrownTwo};
    h2 {
        display: inline;

        font-size: 18px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: 2.22;
        letter-spacing: 0.36px;
        text-align: left;
        color: ${(props) => props.theme.colors.primary};
    }
`;

interface Props {
    searchResult?: Paginated<{
        title: string;
        seoSlug: string;
        image: string;
        imageThumb: string;
        imageThumbLarge: string;
        category: Category;
        description: string;
        phrase: string;
        type: 'article' | 'video' | 'gallery' | 'event';
        additinalInfo?: string;
        dateDisplayed: string;
    }>;
    phrase?: string;
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    latestIssue: Issue;
    companies: Company[];
    props?: string;
    ads: PageAds;
}

export const SearchPage = (props: Props): React.JSX.Element => {
    const router = useRouter();
    const { searchResult, events = [], eventsPatronate = [], latestIssue, companies = [] } = props;
    const [phrase, setPhrase] = useState<string | null>((router.query?.phrase as string) ?? null);
    const [orderBy, setOrderBy] = useState<string | null>((router.query.orderBy as string) ?? null);

    const onPhraseChange = (e: ChangeEvent<HTMLInputElement>) => {
        setPhrase(e.target.value);
    };

    const onOrderByChange = (value: string) => {
        setOrderBy(value);
        const query: Record<string, string | number> = {
            page: 1,
            orderBy: value,
            phrase
        };
        const qs = new URLSearchParams(query as any as string);
        router.push(`?${qs.toString()}`);
    };

    const onSearch = () => {
        if (phrase) {
            const query: Record<string, string | number> = {
                page: 1,
                orderBy,
                phrase
            };
            const qs = new URLSearchParams(query as any as string);
            router.push(`?${qs.toString()}`);
        } else {
            router.push(router.pathname);
        }
    };

    const onPaginate = (page) => {
        const qs = createFromObject({
            page: page,
            orderBy,
            phrase
        });
        router.push(qs);
    };

    const { url, name } = getConfig().publicRuntimeConfig;
    const { rectangle, skyscraper, halfpage, navibox, wideboardBottom } = props.ads;
    return (
        <BasicLayout ads={props.ads}>
            <NextSeo title={`Szukaj | ${name}`} />
            <SiteLinksSearchBoxJsonLd
                url={url}
                potentialActions={[
                    {
                        target: `${url}/szukaj?phrase`,
                        queryInput: 'phrase'
                    }
                ]}
            />
            <Row gutter={16}>
                <Content>
                    <Row>
                        <Col span={24}>
                            <SectionTitle title="Wyszukiwanie" level={1} />
                        </Col>
                        {searchResult?.results > 0 && (
                            <Col span={24}>
                                <SearchText>
                                    Znaleziono {searchResult.results} pozycji odpowiadających
                                    wyszukiwaniu: <h2>{props.phrase}</h2>
                                </SearchText>
                            </Col>
                        )}
                    </Row>
                    <Section>
                        <Col span={24}>
                            <Search
                                placeholder="Wpisz szukaną frazę"
                                size="large"
                                allowClear
                                value={phrase}
                                onChange={onPhraseChange}
                                onPressEnter={onSearch}
                                suffix={
                                    <button onClick={onSearch}>
                                        <SearchOutlined
                                            style={{
                                                fontSize: 16,
                                                color: '#1890ff'
                                            }}
                                        />
                                    </button>
                                }
                            />
                        </Col>
                    </Section>
                    <Section>
                        <Col span={24}>
                            <Space>
                                <span>Sortuj wyniki</span>
                                <Select
                                    style={{ width: 200 }}
                                    value={orderBy}
                                    onChange={onOrderByChange}
                                >
                                    <OptGroup label="Data">
                                        <Option value="byDate.asc">
                                            Wg daty - od najstarszych
                                        </Option>
                                        <Option value="byDate.desc">
                                            Wg daty - od najnowszych
                                        </Option>
                                    </OptGroup>
                                    <OptGroup label="Trafność">
                                        <Option value="byScore.desc">
                                            Wg trafności - malejąco
                                        </Option>
                                        <Option value="byScore.asc">Wg trafności - rosnąco</Option>
                                    </OptGroup>
                                </Select>
                            </Space>
                        </Col>
                    </Section>
                    {searchResult && (
                        <Section>
                            <Col span={24}>
                                <Row gutter={[16, 16]}>
                                    {searchResult.items.map((item, i) => {
                                        if (i === 2) {
                                            return (
                                                <Col span={24} key={item.seoSlug}>
                                                    <Item $item={item} />
                                                    <Row justify="center">
                                                        <Col xs={24} sm={12}>
                                                            <SkyscraperMobile
                                                                id={skyscraper[0]}
                                                                margin="12px auto 12px auto"
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <SkyscraperMobile
                                                                id={skyscraper[1]}
                                                                margin="12px auto 0 auto"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            );
                                        }
                                        if (i === 6) {
                                            return (
                                                <Col span={24} key={item.seoSlug}>
                                                    <Item $item={item} />
                                                    <ExpandNavibox
                                                        id={navibox[0]}
                                                        margin="25px auto"
                                                    />
                                                </Col>
                                            );
                                        }
                                        if (i === 10) {
                                            return (
                                                <Col span={24} key={item.seoSlug}>
                                                    <Item $item={item} />
                                                    <ExpandNavibox
                                                        id={navibox[1]}
                                                        margin="25px auto"
                                                    />
                                                </Col>
                                            );
                                        }
                                        return (
                                            <Col span={24} key={item.seoSlug}>
                                                <Item $item={item} />
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Col>
                            <Col span={24}>
                                <Pagination
                                    currentPage={searchResult.currentPage}
                                    results={searchResult.results}
                                    pageSize={15}
                                    onChange={onPaginate}
                                    next={searchResult.next}
                                    prev={searchResult.prev}
                                />
                            </Col>
                        </Section>
                    )}
                </Content>
                <Sider>
                    <Row gutter={[16, 32]}>
                        <Col span={24}>
                            <Rectangle id={rectangle[0]} />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[0]} />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[1]} />
                        </Col>
                    </Row>
                    <Section gutter={[16, 32]}>
                        <Col span={24}>
                            <EventsBlock events={events} eventsPatronate={eventsPatronate} />
                        </Col>
                    </Section>
                    <Section gutter={[16, 32]}>
                        <Col span={24}>
                            <Rectangle id={rectangle[2]} />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[1]} />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[3]} />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={halfpage[2]} />
                        </Col>
                    </Section>
                </Sider>
            </Row>
            <Divider />
            <Row>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
                <Content>
                    <ExpandNavibox id={navibox[2]} margin="12px auto 12px auto" />
                </Content>
                <Sider>
                    <Rectangle id={rectangle[4]} margin="12px auto 12px auto" />
                </Sider>
            </Row>
            <Divider />
            <Section>
                <Col span={24}>
                    <ContentProvidersSection companies={companies} />
                </Col>
            </Section>
            <Section>
                <Wideboard id={wideboardBottom[0]} />
            </Section>
            <Divider />
            <Section gutter={[16, 32]} justify="center">
                <Col xs={24} md={20}>
                    <Newsletter />
                </Col>
            </Section>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
    const page: string = (query.page || '1') as any as string;
    const orderBy: string = (query.orderBy as any as string) || '';
    const searchPhrase: string = query.phrase as any as string;
    const [key, order] = orderBy.split('.');
    const { apiUrl } = getConfig().publicRuntimeConfig;
    if (searchPhrase) {
        const qs = new URLSearchParams({ page, searchPhrase, [key]: order });

        return Promise.all([
            fetch(`${apiUrl}/public/api/search?${qs.toString()}`),
            fetch(`${apiUrl}/public/api/naszadsik?path=szukaj`)
        ]).then(async ([res1, res2]) => {
            const data = await res1.json();
            if (data.code === 404) {
                return {
                    notFound: true
                };
            }
            const ads = await res2.json();
            return { props: { ...convertProps(ads), ...convertProps(data) } };
        });
    } else {
        return Promise.all([
            fetch(`${apiUrl}/public/api/search`),
            fetch(`${apiUrl}/public/api/naszadsik?path=szukaj`)
        ]).then(async ([res1, res2]) => {
            const data = await res1.json();
            if (data.code === 404) {
                return {
                    notFound: true
                };
            }
            const ads = await res2.json();
            return { props: { ...convertProps(ads), ...convertProps(data) } };
        });
    }
};

export default SearchPage;
